import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

type SpeechRecognitionEventLike = Event & {
  resultIndex: number;
  results: ArrayLike<{
    isFinal: boolean;
    0: { transcript: string };
  }>;
};

type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: Event & { error?: string }) => void) | null;
  onend: (() => void) | null;
};

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

type FeedbackNote = {
  path: string;
  page: string;
  text: string;
};

const OPEN_EVENT = 'open-demo-feedback';
const RECIPIENT = 'cj@maxnetdigital.com';
const MAXNET_LOGO = '/images/maxnet/maxnet-logo-green-horizontal.png';
const CJ_AVATAR = '/images/maxnet/cj-caricature-maxnet.png';

function pageName(pathname: string) {
  if (pathname === '/') return 'Homepage';

  return pathname
    .split('/')
    .filter(Boolean)
    .map((part) =>
      part
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
    )
    .join(' / ');
}

export default function DemoFeedbackRecorder() {
  const { pathname } = useLocation();
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const recordingRef = useRef(false);
  const pathRef = useRef(pathname);
  const [isOpen, setIsOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [notes, setNotes] = useState<FeedbackNote[]>([]);
  const [interim, setInterim] = useState('');
  const [manualText, setManualText] = useState('');
  const [error, setError] = useState('');
  const [emailOpened, setEmailOpened] = useState(false);

  useEffect(() => {
    pathRef.current = pathname;
  }, [pathname]);

  useEffect(() => {
    const open = () => setIsOpen(true);
    window.addEventListener(OPEN_EVENT, open);
    return () => window.removeEventListener(OPEN_EVENT, open);
  }, []);

  useEffect(
    () => () => {
      recordingRef.current = false;
      recognitionRef.current?.abort();
    },
    [],
  );

  const supported =
    typeof window !== 'undefined' &&
    Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);

  const addFinalNote = (text: string) => {
    const clean = text.trim();
    if (!clean) return;
    const path = pathRef.current;
    setNotes((current) => {
      const last = current[current.length - 1];

      if (last?.path === path) {
        return [
          ...current.slice(0, -1),
          { ...last, text: `${last.text} ${clean}`.trim() },
        ];
      }

      return [...current, { path, page: pageName(path), text: clean }];
    });
  };

  const updateNote = (index: number, text: string) => {
    setNotes((current) =>
      current.map((note, noteIndex) => (noteIndex === index ? { ...note, text } : note)),
    );
  };

  const beginRecognition = () => {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) {
      setError('Voice transcription is not available in this browser. You can type your feedback below.');
      return;
    }

    const recognition = new Recognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      let nextInterim = '';

      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        const transcript = result[0]?.transcript ?? '';
        if (result.isFinal) {
          addFinalNote(transcript);
        } else {
          nextInterim += transcript;
        }
      }

      setInterim(nextInterim.trim());
    };

    recognition.onerror = (event) => {
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        recordingRef.current = false;
        setIsRecording(false);
        setIsPaused(false);
        setError('Microphone access was not allowed. Enable microphone access, then try again.');
      } else if (event.error !== 'aborted' && event.error !== 'no-speech') {
        setError('The microphone paused unexpectedly. Tap Resume to continue.');
        setIsPaused(true);
      }
    };

    recognition.onend = () => {
      if (!recordingRef.current) return;
      window.setTimeout(() => {
        if (!recordingRef.current) return;
        try {
          recognition.start();
        } catch {
          // The browser can briefly reject a restart while closing the prior session.
        }
      }, 250);
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const startRecording = () => {
    setError('');
    setEmailOpened(false);
    recordingRef.current = true;
    setIsRecording(true);
    setIsPaused(false);
    setIsOpen(false);
    beginRecognition();
  };

  const pauseRecording = () => {
    recordingRef.current = false;
    recognitionRef.current?.stop();
    setInterim('');
    setIsPaused(true);
  };

  const resumeRecording = () => {
    setError('');
    recordingRef.current = true;
    setIsPaused(false);
    beginRecognition();
  };

  const finishRecording = () => {
    recordingRef.current = false;
    recognitionRef.current?.stop();
    if (interim) addFinalNote(interim);
    setInterim('');
    setIsRecording(false);
    setIsPaused(false);
    setIsOpen(true);
  };

  const cancelRecording = () => {
    recordingRef.current = false;
    recognitionRef.current?.abort();
    setIsRecording(false);
    setIsPaused(false);
    setInterim('');
    setNotes([]);
    setManualText('');
    setError('');
    setIsOpen(false);
  };

  const groupedTranscript = useMemo(() => {
    const groups = new Map<string, FeedbackNote[]>();

    notes.forEach((note) => {
      const key = `${note.page}|${note.path}`;
      groups.set(key, [...(groups.get(key) ?? []), note]);
    });

    const sections = Array.from(groups.entries()).map(([key, pageNotes]) => {
      const [page, path] = key.split('|');
      return [
        `${page} (${window.location.origin}${path})`,
        ...pageNotes.map((note) => `- ${note.text}`),
      ].join('\n');
    });

    if (manualText.trim()) {
      sections.push(`Additional notes\n- ${manualText.trim()}`);
    }

    return sections.join('\n\n');
  }, [manualText, notes]);

  const prepareEmail = () => {
    if (!groupedTranscript.trim()) return;

    const subject = 'Inspire demo website feedback';
    const body = [
      'Hi CJ,',
      '',
      'Here is my feedback from reviewing the Inspire demo site:',
      '',
      groupedTranscript,
      '',
      '— Sent from the Inspire demo feedback recorder',
    ].join('\n');

    setEmailOpened(true);
    window.location.href = `mailto:${RECIPIENT}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const copyTranscript = async () => {
    if (!groupedTranscript.trim()) return;
    await navigator.clipboard.writeText(groupedTranscript);
    setEmailOpened(true);
  };

  return (
    <>
      {(isRecording || isPaused) && !isOpen && (
        <div className="fixed inset-x-3 bottom-4 z-[80] mx-auto max-w-xl overflow-hidden rounded-2xl border border-[#35de31]/35 bg-[#08090d] text-white shadow-[0_20px_60px_rgba(0,0,0,0.45),0_0_30px_rgba(53,222,49,0.16)]">
          <div className="grid gap-3 px-4 py-3 sm:grid-cols-[auto_1fr_auto] sm:items-center">
            <div className="flex min-w-0 items-center gap-3">
              <img
                src={CJ_AVATAR}
                alt=""
                className="h-12 w-12 shrink-0 rounded-xl border border-[#35de31]/35 bg-[#12141c] object-cover object-top"
              />
              <span
                className={`h-3 w-3 shrink-0 rounded-full ${isPaused ? 'bg-yellow-300' : 'bg-[#35de31] animate-pulse'}`}
                aria-hidden="true"
              />
              <div className="min-w-0">
                <p className="text-sm font-semibold">
                  {isPaused ? 'Feedback paused' : 'Recording website feedback'}
                </p>
                <p className="truncate text-xs text-white/55">
                  {interim || `Current page: ${pageName(pathname)}`}
                </p>
              </div>
            </div>
            <div className="hidden sm:block" />
            <div className="grid grid-cols-2 gap-3 sm:flex sm:justify-end">
              <button
                type="button"
                onClick={isPaused ? resumeRecording : pauseRecording}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white hover:border-[#35de31]/70 hover:bg-white/10"
              >
                <i className={isPaused ? 'ri-play-line text-lg text-[#35de31]' : 'ri-pause-line text-lg text-[#35de31]'} />
                {isPaused ? 'Resume' : 'Pause'}
              </button>
              <button
                type="button"
                onClick={finishRecording}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-[#35de31] px-5 py-3 text-sm font-extrabold text-black shadow-[0_10px_28px_rgba(53,222,49,0.24)] hover:bg-[#43e83f]"
              >
                <i className="ri-check-line text-lg" />
                Finish
              </button>
            </div>
          </div>
        </div>
      )}

      {isOpen && (
        <div
          className="fixed inset-0 z-[90] flex items-end justify-center bg-foreground-950/65 p-0 backdrop-blur-sm sm:items-center sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="demo-feedback-title"
        >
          <div className="max-h-[92svh] w-full max-w-2xl overflow-y-auto rounded-t-3xl border border-[#35de31]/25 bg-[#08090d] p-0 text-white shadow-[0_30px_90px_rgba(0,0,0,0.55),0_0_45px_rgba(53,222,49,0.16)] sm:rounded-3xl">
            <div className="relative overflow-hidden border-b border-white/10 bg-[#0d0f16] p-6 sm:p-7">
              <div className="absolute -right-16 -top-20 h-52 w-52 rounded-full bg-[#35de31]/16 blur-3xl" aria-hidden="true" />
              <div className="relative flex items-start justify-between gap-4">
                <div className="flex min-w-0 items-start gap-4">
                  <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-2xl border border-[#35de31]/35 bg-[#12141c]">
                    <img
                      src={CJ_AVATAR}
                      alt="CJ Ryan"
                      className="h-full w-full object-cover object-top"
                    />
                    <span className="absolute bottom-2 right-2 h-3 w-3 rounded-full border-2 border-[#08090d] bg-[#35de31]" />
                  </div>
                  <div className="min-w-0">
                    <img
                      src={MAXNET_LOGO}
                      alt="MaxNet Digital"
                      className="h-auto w-44 max-w-full object-contain"
                    />
                    <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#35de31]">
                      Private demo review
                    </p>
                    <h2 id="demo-feedback-title" className="mt-2 text-3xl font-bold leading-tight text-white">
                      Tell CJ what you&apos;d change.
                    </h2>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="rounded-full border border-white/10 bg-white/5 p-2 text-white/70 hover:border-[#35de31]/60 hover:text-white"
                  aria-label="Close feedback recorder"
                >
                  <i className="ri-close-line text-2xl" />
                </button>
              </div>
            </div>

            <div className="p-6 sm:p-8">
            {!notes.length && !isRecording && !isPaused ? (
              <>
                <p className="text-sm leading-relaxed text-white/70">
                  Start recording, then browse the website normally and describe any changes you want.
                  The recorder will organize your comments by page. When you're finished, review the
                  transcript and send it directly to CJ.
                </p>

                <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                  <p className="text-sm font-semibold text-white">Before you begin</p>
                  <ul className="mt-3 space-y-2 text-sm text-white/65">
                    <li className="flex gap-2">
                      <i className="ri-checkbox-circle-line mt-0.5 text-[#35de31]" />
                      <span>Allow microphone access when your browser asks.</span>
                    </li>
                    <li className="flex gap-2">
                      <i className="ri-checkbox-circle-line mt-0.5 text-[#35de31]" />
                      <span>The recording controls appear only after you start.</span>
                    </li>
                    <li className="flex gap-2">
                      <i className="ri-checkbox-circle-line mt-0.5 text-[#35de31]" />
                      <span>You can pause, resume, or cancel at any time.</span>
                    </li>
                  </ul>
                </div>

                {error && <p className="mt-4 text-sm text-red-700">{error}</p>}

                <button
                  type="button"
                  onClick={startRecording}
                  disabled={!supported}
                  className="mt-6 inline-flex min-h-16 w-full items-center justify-center gap-3 rounded-2xl bg-[#35de31] px-6 py-5 text-lg font-extrabold text-black shadow-[0_14px_36px_rgba(53,222,49,0.26)] transition-colors hover:bg-[#43e83f] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <i className="ri-mic-line text-2xl" />
                  Start recording feedback
                </button>

                {!supported && (
                  <p className="mt-3 text-center text-xs text-white/50">
                    Voice transcription is unavailable in this browser. You can still type feedback below.
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="text-sm text-white/70">
                  Review and correct the transcript below. Tap directly into any comment to fix a word
                  before preparing the email to CJ.
                </p>

                <div className="mt-5 max-h-72 space-y-4 overflow-y-auto rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                  {notes.length ? (
                    notes.map((note, index) => (
                      <div key={`${note.path}-${index}`}>
                        <p className="text-[10px] uppercase tracking-[0.16em] text-[#35de31]">
                          {note.page}
                        </p>
                        <textarea
                          value={note.text}
                          onChange={(event) => updateNote(index, event.target.value)}
                          rows={Math.max(2, Math.ceil(note.text.length / 42))}
                          aria-label={`Edit transcript for ${note.page}`}
                          className="mt-1 w-full resize-y rounded-lg border border-transparent bg-[#11141c] px-3 py-2 text-sm leading-relaxed text-white outline-none hover:border-white/15 focus:border-[#35de31]"
                        />
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-white/50">No voice transcript was captured.</p>
                  )}
                </div>
              </>
            )}

            <label className="mt-5 block text-sm font-semibold text-white" htmlFor="demo-feedback-notes">
              Additional notes
            </label>
            <textarea
              id="demo-feedback-notes"
              value={manualText}
              onChange={(event) => setManualText(event.target.value)}
              rows={4}
              placeholder="Type anything else you want CJ to know..."
              className="mt-2 w-full rounded-xl border border-white/10 bg-[#11141c] px-4 py-3 text-sm text-white outline-none placeholder:text-white/35 focus:border-[#35de31]"
            />

            {(notes.length > 0 || manualText.trim()) && (
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={prepareEmail}
                  className="inline-flex min-h-16 items-center justify-center gap-3 rounded-2xl bg-[#35de31] px-5 py-5 text-base font-extrabold text-black shadow-[0_14px_36px_rgba(53,222,49,0.22)] hover:bg-[#43e83f]"
                >
                  <i className="ri-mail-send-line text-xl" />
                  Prepare email to CJ
                </button>
                <button
                  type="button"
                  onClick={copyTranscript}
                  className="inline-flex min-h-16 items-center justify-center gap-3 rounded-2xl border border-white/20 bg-white/[0.06] px-5 py-5 text-base font-bold text-white hover:border-[#35de31]/70 hover:bg-white/10"
                >
                  <i className="ri-file-copy-line text-xl text-[#35de31]" />
                  Copy transcript
                </button>
              </div>
            )}

            {emailOpened && (
              <p className="mt-3 text-center text-xs text-white/50">
                Your feedback has been prepared. Review it in your email app and tap Send.
              </p>
            )}

            <button
              type="button"
              onClick={cancelRecording}
              className="mt-5 inline-flex min-h-12 w-full items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm font-semibold text-white/70 hover:border-white/25 hover:text-white"
            >
              Cancel and erase this feedback
            </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export { OPEN_EVENT as DEMO_FEEDBACK_OPEN_EVENT };
